<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="room-type-list content-wrapper">
      <div class="page-header">{{ $t('contactPerson') }}</div>
      <div>
        <el-row>
          <el-col :span="24">
            <router-link to="contact-person/add" style="float: right">
              <el-button type="primary" icon="el-icon-plus">{{ $t('newContactPerson') }}</el-button>
            </router-link>
          </el-col>
        </el-row>
        <div style="margin-top: 25px">
          <el-table
            id="room-type-list-table"
            :data="contactPersonList"
            header-row-class-name="table-header-color"
            style="width: 100%">
            <el-table-column
              :label="$t('primary')"
              width="90px">
              <template slot-scope="scope">
                <el-radio v-model="primary" :label="scope.row.id" @change="onPriorityChange"> &nbsp; </el-radio>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('secondary')"
              width="90px">
              <template slot-scope="scope">
                <el-checkbox v-model="secondaryList[scope.row.id]" :label="scope.row.id" @change="onContactPersonCheckChange('secondary', scope.row.id)"> &nbsp; </el-checkbox>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('stopSale')"
              width="90px">
              <template slot-scope="scope">
                <el-checkbox v-model="stopSaleList[scope.row.id]" :label="scope.row.id" @change="onContactPersonCheckChange('stopSale', scope.row.id)"> &nbsp; </el-checkbox>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('name')">
              <template slot-scope="scope">{{ getContactName(scope.row) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('position')">
              <template slot-scope="scope">{{ bindData(scope.row, 'position') }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('phone')">
              <template slot-scope="scope">{{ bindData(scope.row, 'phone') }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('mobile')">
              <template slot-scope="scope">
                <span v-if="scope.row.mobileList.length === 0">-</span>
                <div v-for="(mobile, index) of scope.row.mobileList" :key="`mobile-${index}`">
                  <span> {{ mobile }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('email')">
              <template slot-scope="scope">
                <span v-if="scope.row.emailList.length === 0">-</span>
                <div v-for="(email, index) of scope.row.emailList" :key="`email-${index}`">
                  <span> {{ email }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column align="center">
              <template slot-scope="scope">
                <el-button type="primary" size="small" icon="el-icon-edit" plain circle @click="onEditButtonClick(scope.row)"></el-button>
                <el-button type="danger" size="small" icon="el-icon-delete" plain circle @click="onRemoveContactPersonButtonClick(scope.row)"></el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </ContentContainer>
</template>

<script>
import i18nMessages from '@/translations/Agent/ManageAgent/ContactPerson/List.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import AgentContactPersonApi from '@/api/agent/contact-person-api'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import { mapState } from 'vuex'
import { bindData } from '@/helpers'
import { get } from 'lodash'

export default {
  name: 'AgentContactPersonList',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { ContentContainer },
  i18n: { messages: i18nMessages },
  created: async function () {
    if (this.activeAgentId === undefined || this.activeAgentId === '') {
      return this.$router.push({
        name: 'AgentList'
      })
    }

    try {
      this.isLoading = true
      const contactPersonList = await AgentContactPersonApi.list(this.activeAgentId)
      const _initContactPersonData = (item) => {
        const emailList = get(item, 'emailList', [])
        const mobileList = get(item, 'mobileList', [])

        item.emailList = emailList
        item.mobileList = mobileList

        return item
      }
      this.contactPersonList = contactPersonList.map(_initContactPersonData)

      const _sortByName = (prev, next) => {
        return (prev.firstName < next.firstName) ? -1 : 1
      }
      this.contactPersonList.sort(_sortByName)

      this._setPriority()
      this.stopSaleList = this._prepareStopSaleList()
      this.secondaryList = this._prepareSecondaryList()
    } catch (err) {
      this.alertError(err.message)
    }

    this.isLoading = false
  },
  computed: {
    ...mapState({
      activeAgentId: state => state.general.activeAgentId
    })
  },
  data () {
    return {
      isLoading: true,
      contactPersonList: [],
      primary: '',
      stopSaleList: {},
      secondaryList: {},
      contactPerson: {
        id: '',
        title: 'Mr.',
        firstName: '',
        lastName: '',
        position: '',
        phone: '',
        mobileList: [''],
        fax: '',
        emailList: ['']
      }
    }
  },
  methods: {
    getContactName: function (contactPerson) {
      return `${bindData(contactPerson, 'title', '')} ${contactPerson.firstName} ${bindData(contactPerson, 'lastName', '')}`
    },
    onEditButtonClick: function (contactPerson) {
      this.contactPerson = this._prepareData(contactPerson)

      this.$router.push({
        name: 'AgentContactPersonEdit',
        params: {
          contactPerson: this.contactPerson
        }
      })
    },
    _prepareData: function (contactPerson) {
      const emailList = get(contactPerson, 'emailList', [''])
      const mobileList = get(contactPerson, 'mobileList', [''])

      const dataList = {
        emailList: emailList,
        mobileList: mobileList
      }

      const result = Object.assign({}, contactPerson, dataList)
      return result
    },
    onPriorityChange: async function () {
      this.startLoading()

      try {
        await this._updatePriority()
        const title = this.$t('updatePrioritySuccessTitle')
        const message = this.$t('updatePrimarySuccessMessage')

        this.showNotificationSuccess(title, message)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    _updatePriority: async function () {
      const data = {
        primary: this.primary
      }

      await AgentContactPersonApi.setPriority(this.activeAgentId, data)
      const _setFlag = (item) => {
        item.isPrimary = 0

        if (item.id === this.primary) {
          item.isPrimary = 1
        }

        return item
      }

      this.contactPersonList.map(_setFlag)
    },
    onContactPersonCheckChange: async function (checkBoxType, contactPersonId) {
      switch (checkBoxType) {
        case 'secondary':
          this._updateIsSecondary(contactPersonId)
          break
        case 'stopSale':
          this._updateStopSate(contactPersonId)
          break
      }
    },
    _updateStopSate: async function (contactPersonId) {
      this.startLoading()

      try {
        const stopSale = (this.stopSaleList[contactPersonId] === true) ? 1 : 0
        const data = {
          stopSale: stopSale
        }
        await AgentContactPersonApi.stopSale(this.activeAgentId, contactPersonId, data)
        const _setFlagStopSale = _setFlagBuilder(contactPersonId, 'stopSale', stopSale)

        this.contactPersonList.map(_setFlagStopSale)

        const title = this.$t('updateStopSaleSuccessTitle')
        const message = this.$t('updateStopSaleSuccessMessage')
        this.showNotificationSuccess(title, message)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    _updateIsSecondary: async function (contactPersonId) {
      this.startLoading()

      try {
        const isSecondary = (this.secondaryList[contactPersonId] === true) ? 1 : 0
        const data = {
          isSecondary: isSecondary
        }
        await AgentContactPersonApi.isSecondary(this.activeAgentId, contactPersonId, data)
        const _setFlagSecondary = _setFlagBuilder(contactPersonId, 'isSecondary', isSecondary)

        this.contactPersonList.map(_setFlagSecondary)

        const title = this.$t('updatePrioritySuccessTitle')
        const message = this.$t('updateSecondarySuccessMessage')
        this.showNotificationSuccess(title, message)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    _setPriority: function () {
      this.contactPersonList.forEach((contactPerson) => {
        if (contactPerson.isPrimary === 1) {
          this.primary = contactPerson.id
        }
      })
    },
    _prepareStopSaleList: function () {
      const stopSaleList = {}
      this.contactPersonList.forEach((contactPerson) => {
        const contactPersonId = contactPerson.id
        stopSaleList[contactPersonId] = Boolean(get(contactPerson, 'stopSale', false))
      })

      return stopSaleList
    },
    _prepareSecondaryList: function () {
      const secondaryList = {}
      this.contactPersonList.forEach((contactPerson) => {
        const contactPersonId = contactPerson.id
        secondaryList[contactPersonId] = Boolean(get(contactPerson, 'isSecondary', false))
      })

      return secondaryList
    },
    onRemoveContactPersonButtonClick: async function (row) {
      const confirmMessage = this.$t('confirmRemoveContactPersonMessage', {
        name: this.getContactName(row)
      })
      const confirmButtonText = this.$t('confirmButtonText')
      const cancelButtonText = this.$t('cancelButtonText')
      const isConfirmRemove = await this.alertConfirmDelete(confirmMessage, confirmButtonText, cancelButtonText)

      if (!isConfirmRemove) {
        return false
      }

      this.removeContactPerson(row.id)
    },
    removeContactPerson: async function (contactPersonId) {
      try {
        this.startLoading()

        await AgentContactPersonApi.remove(this.activeAgentId, contactPersonId)

        this.contactPersonList = this.contactPersonList.filter(item => item.id !== contactPersonId)

        const notificationTitle = this.$t('removeContactPersonSuccess')
        this.showNotificationSuccess(notificationTitle)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    bindData: function (row, key) {
      return bindData(row, key)
    }
  }
}

const _setFlagBuilder = (contactPersonId, key, value) => {
  return (item) => {
    if (item.id === contactPersonId) {
      item[key] = value
    }

    return item
  }
}

</script>

<style scoped>
  #room-type-list-table {
    width: 100%;
    border: 1px solid #dddddd;
    border-radius: 5px;
    box-shadow: 0px 3px 5px 1px #eeeeee;
  }
  .el-table th {
    background-color: #eeeeee;
  }
  .el-form > div {
    text-align: left;
  }
</style>
